import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useMemo,
  useEffect,
} from "react";

import api from "../services/api";
import { useAuth } from "../hooks/AuthProvider";
import { storageService } from "../services/storageService";

interface CompanyParams {
  name: string;
  has_cobransaas: boolean;
  card_password_length: number;
  cnpj: string;
  has_change_password: boolean;
  crypt_key: boolean;
  has_security: boolean;
  has_annuity: boolean;
  has_change_card_password_confirmation: boolean;
  has_cpf_required: boolean;
}

interface CompanyParamsContextType {
  companyParams: CompanyParams | null;
  isLoading: boolean;
  error: string | null;
}

export const CompanyParamsContext = createContext<
  CompanyParamsContextType | undefined
>(undefined);

export const CompanyParamsProvider: React.FC = ({ children }) => {
  const { user } = useAuth();

  const [companyParams, setCompanyParams] = useState<CompanyParams | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanyParams = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get("get_company_params");
      setCompanyParams(response);
      storageService.setCpfRequired(response?.has_cpf_required);
    } catch (err) {
      setError("Erro ao carregar os parâmetros da empresa");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      companyParams,
      isLoading,
      error,
      fetchCompanyParams,
    }),

    [companyParams, isLoading, error]
  );

  useEffect(() => {
    if (user) {
      fetchCompanyParams();
    }
  }, [user]);

  return (
    <CompanyParamsContext.Provider value={value}>
      {children}
    </CompanyParamsContext.Provider>
  );
};

export const CompanyParams = (): CompanyParamsContextType => {
  const context = useContext(CompanyParamsContext);
  if (context === undefined) {
    throw new Error(
      "useCompanyParams must be used within a CompanyParamsProvider"
    );
  }
  return context;
};
